import { Button, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import React from 'react';
import { useCommonWebTranslation } from '../useCommonWebTranslation';
/**
 * Renders a logout warning dialog component that informs the user when their session is about to expire due to inactivity.
 * Automatically tracks user idle time and handles logout behavior accordingly.
 *
 * @param {LogOutWarningDialogProps} root0 - Component props.
 * @param {number} root0.sessionTimeoutMs - The total session timeout duration in milliseconds.
 * @param {number} root0.idleForMs - The amount of time the user has already been idle (in milliseconds).
 * @param {() => void} root0.onLoggedOutAcknowledge - Callback when the user acknowledges session expiration.
 * @param {() => void} root0.onSessionRefresh - Callback to refresh the session (keep user logged in).
 * @param {() => void} root0.onLogoutClick - Callback when the user explicitly chooses to log out.
 * @param {() => Promise<void>} [root0.onBeforeSessionExpiredDialogShow] - Optional async callback fired before showing session expired message.
 *
 * @returns {JSX.Element} The rendered React component for the logout warning dialog content.
 */
export const LogoutWarningContent = ({ onLoggedOutAcknowledge, onSessionRefresh, onLogoutClick, idleForMs, sessionTimeoutMs, onBeforeSessionExpiredDialogShow, }) => {
    const [idleMs, setIdleMs] = React.useState(idleForMs);
    const [loggedOut, setLoggedOut] = React.useState(idleMs >= sessionTimeoutMs);
    const { t } = useCommonWebTranslation();
    const hasRunningPromise = React.useRef(false);
    React.useEffect(() => {
        if (idleMs >= sessionTimeoutMs && !loggedOut) {
            if (onBeforeSessionExpiredDialogShow && !hasRunningPromise.current) {
                onBeforeSessionExpiredDialogShow()
                    .then(() => {
                    setLoggedOut(true);
                    return;
                })
                    .finally(() => {
                    hasRunningPromise.current = false;
                });
                hasRunningPromise.current = true;
            }
            else if (!onBeforeSessionExpiredDialogShow) {
                setLoggedOut(true);
            }
        }
        else if (idleMs < sessionTimeoutMs && loggedOut) {
            setLoggedOut(false);
        }
    }, [idleMs, sessionTimeoutMs, loggedOut, onBeforeSessionExpiredDialogShow]);
    React.useEffect(() => {
        const handle = setTimeout(() => {
            if (!loggedOut) {
                setIdleMs((prevState) => prevState + 1000);
            }
        }, 1000);
        return () => {
            clearTimeout(handle);
        };
    }, [idleMs, loggedOut]);
    return (React.createElement(React.Fragment, null,
        React.createElement(DialogTitle, null,
            React.createElement(React.Fragment, null,
                !loggedOut && t('userIdleMonitor.continueWorking'),
                loggedOut && t('userIdleMonitor.sessionExpired'))),
        React.createElement(DialogContent, null,
            React.createElement(DialogContentText, null,
                React.createElement(React.Fragment, null,
                    !loggedOut && t('userIdleMonitor.doYouWantToContinue', { item: Math.round((sessionTimeoutMs - idleMs) / 1000) }),
                    loggedOut && t('userIdleMonitor.loggedOut')))),
        React.createElement(DialogActions, null,
            !loggedOut && (React.createElement(React.Fragment, null,
                React.createElement(Button, { color: 'primary', onClick: () => {
                        onSessionRefresh();
                    } }, t('userIdleMonitor.continue')),
                React.createElement(Button, { color: 'primary', onClick: () => {
                        onLogoutClick();
                    } }, t('userIdleMonitor.logOut')))),
            loggedOut && (React.createElement(Button, { color: 'primary', onClick: () => onLoggedOutAcknowledge() }, t('userIdleMonitor.ok'))))));
};
